// Make changes › More actions › After saving… — "When a record is added /
// changed / deleted, email (or WhatsApp / text) the person in <field>,
// saying …" ⇄ a `follow_up_actions[]` send entry plus the starter template it
// sends (Metadata_Model/message_templates/<obj>/<id>.json, saved through the
// host). Anything else after saving is described by the AI. Pure functions.

import type { FollowupActionDef } from '../../txn/types'
import { normalizeFollowupAction } from '../../txn/types'
import type { MessageTemplate } from '../../messaging/types'

export type AfterEvent = 'on-add' | 'on-edit' | 'on-add-edit' | 'on-delete'
export type AfterChannel = 'email' | 'whatsapp' | 'sms'

export const AFTER_EVENTS: { id: AfterEvent; label: string }[] = [
  { id: 'on-add', label: 'is added' },
  { id: 'on-edit', label: 'is changed' },
  { id: 'on-add-edit', label: 'is added or changed' },
  { id: 'on-delete', label: 'is deleted' },
]
export const AFTER_CHANNELS: { id: AfterChannel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'sms', label: 'Text message' },
]

/** "{Claim no}" (a field's name on the form) → "{claim_no}" (the token the
 *  template engine merges). Names it does not know are left as written. */
export function namesToTokens(text: string, cols: { db_name?: string; name?: string; heading?: string }[]): string {
  return String(text || '').replace(/\{([^{}]+)\}/g, (all, inner: string) => {
    const want = inner.trim().toLowerCase()
    const c = cols.find((x) => [x.heading, x.db_name, x.name].some((k) => String(k || '').trim().toLowerCase() === want))
    return c ? `{${c.db_name || c.name}}` : all
  })
}

export function tokensToNames(text: string, cols: { db_name?: string; name?: string; heading?: string }[]): string {
  return String(text || '').replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (all, inner: string) => {
    const c = cols.find((x) => String(x.db_name || x.name || '').toLowerCase() === inner.toLowerCase())
    return c?.heading ? `{${c.heading}}` : all
  })
}

export function templateId(objName: string, existing: string[]): string {
  const base = `${objName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_after_saving`
  let id = base
  for (let n = 2; existing.includes(id); n++) id = `${base}_${n}`
  return id
}

/** The starter template an After-saving send uses. */
export function afterTemplate(opts: {
  id: string; objName: string; title: string; channel: AfterChannel; toColumn: string; text: string
}): MessageTemplate {
  const lines = opts.text.split('\n')
  const body = { type: 'doc' as const, content: lines.map((line) => (line
    ? { type: 'paragraph', content: [{ type: 'text', text: line }] } : { type: 'paragraph' })) }
  const channels: MessageTemplate['channels'] = opts.channel === 'email'
    ? { email: { subject: opts.text.split('\n')[0].slice(0, 120) || opts.title, body, attachments: [] } }
    : { [opts.channel]: { text: opts.text, attachments: [] } }
  return {
    id: opts.id,
    name: opts.title,
    obj_name: opts.objName,
    recipients: [{ role: 'to', channel: opts.channel, from: { column: opts.toColumn } }],
    channels,
  }
}

/** The follow-up entry that sends `templateId` after `event`. */
export function afterFollowup(opts: {
  name: string; event: AfterEvent; channel: AfterChannel; templateId: string
}): FollowupActionDef {
  return {
    event: opts.event,
    name: opts.name,
    send: { enabled: true, channels: [opts.channel], templates: [opts.templateId], template: opts.templateId },
  }
}

/** One follow-up in words. */
export function afterText(a: unknown, label: (col: string) => string, templateTo?: string): string {
  const def = normalizeFollowupAction(a as never)
  const ev = String(def.event || '')
  const when = ev.startsWith('on-action:') ? `after the ${ev.slice('on-action:'.length)} button`
    : `when a record ${AFTER_EVENTS.find((e) => e.id === ev)?.label ?? ev.replace(/^on-/, '').replace(/-/g, ' or ')}`
  if (def.send) {
    const ch = def.send.channels?.[0] || 'email'
    const how = AFTER_CHANNELS.find((c) => c.id === ch)?.label.toLowerCase() || ch
    return `Send a${how === 'email' ? 'n' : ''} ${how} ${when}${templateTo ? ` to the person in ${label(templateTo)}` : ''}`
  }
  if (def.ioflow_event) return `Tell the connected services ${when}`
  if (def.wizard) return `Run the ${def.wizard.obj_name} wizard ${when}`
  if (def.business_logic?.method) return `Run ${def.business_logic.description || def.business_logic.method} ${when}`
  return `${def.name} ${when}`
}
